import './checkout_delivery.css';
import { DeliveryIcon } from "../../assets/icons/DeliveryIcon";
import { CheckoutFormStep } from "../CheckoutFormStep/CheckoutFormStep";
import { useCheckout } from '../../context/CheckoutContext';
import { useEffect, useRef, useState } from 'react';
import { useBusiness } from '../../context/BusinessContext';
import { DeliveryMethod } from '../../Enum/DeliveryMethod';
import { AppleMap } from '../AppleMap/AppleMap';

import * as turf from "@turf/turf";

export const CheckoutDelivery = () => {
    //HOOKS
    const {
        pickupSettings,
        eatInSettings,
        deliverySettings,
        selectedDeliveryMethod,
        setSelectedDeliveryMethod,
        setCheckoutStep,
        address,
        setAddress,
        apartment,
        setApartment,
        floor,
        setFloor,
        entrance,
        setEntrance,
        latitude,
        setLatitude,
        longitude,
        setLongitude,
        deliveryAreas,
        setDeliveryPrice,
        customerLatitude,
        setCustomerLatitude,
        customerLongitude,
        setCustomerLongitude,
        postCode,
        setPostCode,
    } = useCheckout();
    const { businessLocation, businessInfo, } = useBusiness();

    //STATES
    const [addressError, setAddressError] = useState("");
    const [userAnnotation, setUserAnnotation] = useState(null);

    //REFS
    const mapRef = useRef();

    //FUNCTIONS
    const continueStep = () => {
        if (selectedDeliveryMethod === DeliveryMethod.DELIVERY && address === "") {
            setAddressError("We were unable to find the address");
            return;
        }
        setCheckoutStep(1);
    }

    const distanceCoordinates = (lat1, lat2, long1, long2) => {
        const R = 6371e3;

        const rad1 = lat1 * Math.PI /180;
        const rad2 = lat2 * Math.PI /180;

        const deltalat = (lat2 - lat1) * Math.PI / 180;
        const detlalon = (long2 - long1) * Math.PI / 180;

        const a = Math.sin(deltalat / 2) * Math.sin(deltalat / 2) + Math.cos(rad1) * Math.cos(rad2) * Math.sin(detlalon / 2) * Math.sin(detlalon / 2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

        return R * c;
    }

    const inArea = (area, lat, long) => {
        if (!area.enable) {
            return false;
        }

        if (area.delivery_type === 0) {
            let distance = distanceCoordinates(latitude, lat, longitude, long);
            let coverage = parseFloat(area.value) * 1609.34;
            return distance <= coverage;
        } else if (area.delivery_type === 1) {
            let coordinates = area.value.split(",");
            let Polygon = [];

            for (let i = 0; i < coordinates.length; i++) {
                let value = coordinates[i].split(" ");
                Polygon.push([parseFloat(value[0]), parseFloat(value[1])]);
            }

            Polygon.push(Polygon[0]);

            let point = turf.point([lat, long]);
            let PolygonT = turf.polygon([Polygon], {name: 'poly1'});

            return turf.booleanPointInPolygon(point, PolygonT);
        }
    }

    const checkCoverage = (latitude, longitude) => {
        let selectedArea = null;

        for (let i = 0; i < deliveryAreas.length; i++) {
            if (inArea(deliveryAreas[i], latitude, longitude)) {
                selectedArea = deliveryAreas[i];
                break;
            }
        }

        if (selectedArea === null) {
            setAddressError("Sorry, we don't deliver to this location");
            setDeliveryPrice(0);
        } else {
            setDeliveryPrice(selectedArea.delivery_charge);
        }
    }

    //EFFECTS
    useEffect(() => {
        if (businessLocation !== null) {
            setLongitude(parseFloat(businessLocation.longitude));
            setLatitude(parseFloat(businessLocation.latitude));
        }
    }, [businessLocation]);

    useEffect(() => {
        let annotation = null;
        if (businessInfo !== null && latitude !== 0 && longitude !== 0) {
            setTimeout(() => {
                mapRef.current.addAnnotation(parseFloat(latitude), parseFloat(longitude), businessInfo.name, (an) => {
                    annotation = an;
                });
                mapRef.current.centerToCoordinates(latitude, longitude, 13);
            }, 500);
        }

        return () => {
            if (mapRef.current && annotation !== null) {
                mapRef.current.removeAnnotation(annotation);
            }
        }
    }, [businessInfo, latitude, longitude]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (address !== "") {
                mapRef.current.geoCoder(address, (res) => {
                    if (res === false) {
                        setAddressError("We were unable to find the address");

                        if (userAnnotation !== null) {
                            mapRef.current.removeAnnotation(userAnnotation);
                            setUserAnnotation(null);
                            mapRef.current.centerToCoordinates(latitude, longitude, 13);
                        }

                    } else {
                        setCustomerLatitude(res.coordinate.latitude);
                        setCustomerLongitude(res.coordinate.longitude);
                        setPostCode(res.postCode);

                        setAddressError("");
                        if (userAnnotation !== null) {
                            mapRef.current.removeAnnotation(userAnnotation); 
                        }

                        mapRef.current.addAnnotation(parseFloat(res.coordinate.latitude), parseFloat(res.coordinate.longitude), "Deliver here", (an) => {
                            setUserAnnotation(an);
                        });
                        mapRef.current.centerToCoordinates(res.coordinate.latitude, res.coordinate.longitude, 13);
                        checkCoverage(res.coordinate.latitude, res.coordinate.longitude);
                    }
                });
            }
        }, 800);
        
        return () => clearTimeout(delayDebounceFn)
    }, [address]);

    return (
        <CheckoutFormStep
            step={0}
            title={"Delivery Method"}
            icon={<DeliveryIcon />}
        >
            <div className='checkout_delivery_selector'>
                {
                    deliverySettings && deliverySettings.enable && (
                        <button className={(selectedDeliveryMethod === DeliveryMethod.DELIVERY) ? 'active' : ''} onClick={() => setSelectedDeliveryMethod(DeliveryMethod.DELIVERY)}>
                            Delivery
                        </button>
                    )
                }
                {
                    pickupSettings && pickupSettings.enable && (
                        <button className={(selectedDeliveryMethod === DeliveryMethod.PICKUP) ? 'active' : ''} onClick={() => setSelectedDeliveryMethod(DeliveryMethod.PICKUP)}>
                            Pickup
                        </button>
                    )
                }
                {
                    eatInSettings && eatInSettings.enable && (
                        <button className={(selectedDeliveryMethod === DeliveryMethod.EAT_IN) ? 'active' : ''} onClick={() => setSelectedDeliveryMethod(DeliveryMethod.EAT_IN)}>
                            Eat in
                        </button>
                    )
                }
            </div>

            <div className='checkout_delivery_method_container' style={{ display: (selectedDeliveryMethod === DeliveryMethod.DELIVERY) ? "block": "none"}}>
                <div className='checkout_input_container'>
                    <label>
                        Delivey Address
                    </label>
                    <input type='text' value={address} onChange={(e) => setAddress(e.target.value)} />
                    <span>{addressError}</span>
                </div>
                <div className='checkout_input_group'>
                    <div className='checkout_input_container'>
                        <label>
                            Apartment
                        </label>
                        <input type='text' value={apartment} onChange={(e) => setApartment(e.target.value)} />
                    </div>
                    <div className='checkout_input_container'>
                        <label>
                            Floor
                        </label>
                        <input type='text' value={floor} onChange={(e) => setFloor(e.target.value)} />
                    </div>
                    <div className='checkout_input_container'>
                        <label>
                            Entrance
                        </label>
                        <input type='text' value={entrance} onChange={(e) => setEntrance(e.target.value)} />
                    </div>
                </div>
                <div className='checkout_delivery_map'>
                    <AppleMap
                        id={'delivery_checkout_map'}
                        lat={0}
                        long={0}
                        ref={mapRef}
                    />
                </div>
            </div>

            {
                selectedDeliveryMethod === DeliveryMethod.PICKUP &&
                (
                    <div className='checkout_delivery_method_container pickup_delivery_method_container'>
                        <div className='item'>
                            <h2>Pickup address</h2>
                            <p>
                                {businessLocation && businessLocation.address}
                            </p>
                        </div>
                        <div className='item'>
                            <h2>Pickup Estimated Time</h2>
                            <p>
                                {pickupSettings && pickupSettings.eta} Minutes
                            </p>
                        </div>
                    </div>
                )
            }
            
            {
                selectedDeliveryMethod === DeliveryMethod.EAT_IN &&
                (
                    <div className='checkout_delivery_method_container pickup_delivery_method_container'>
                        <div className='item'>
                            <h2>Eat in address</h2>
                            <p>
                                {businessLocation && businessLocation.address}
                            </p>
                        </div>
                    </div>
                )
            }
            
            <div className='checkout_delivery_continue_container'>
                <button onClick={continueStep}>
                    Continue
                </button>
            </div>
        </CheckoutFormStep>
    );
}